import React, { useState, useEffect } from 'react'
import { storeAuthToken, storeOrganizerId, storeUserDetails, getAuthToken } from '../../utils/auth'
import { Link, useNavigate } from 'react-router-dom'
import { loginRequestOTP, userLogin, loginVerifyOTP } from '../../api'
import { OtpPinInput } from '../../components/OtpPinInput'
import { ShieldCheck, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { images } from '../../assets/images'
import Background from './Background'
import './LoginPage.css'

type LoginPageProps = {}

/** Venue role id (venue admin). Coerce API number/string safely. */
const VENUE_ROLE = "3";

const isVenueAccount = (user: any) => {
  const primary = String(user?.userRole ?? "");
  const roles = (user?.userRoles || []).map((r: string | number) => String(r));
  return primary === VENUE_ROLE || roles.includes(VENUE_ROLE);
};

/** Clear any session that was started with the wrong account type. */
const clearVenueSession = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem("token");
  localStorage.removeItem("authToken");
  localStorage.removeItem("authUser");
  localStorage.removeItem("venueData");
  localStorage.removeItem("organizerId");
  localStorage.removeItem("eventData");
  localStorage.removeItem("venueId");
  localStorage.removeItem("userData");
};

/** Map raw API / validation strings into clear venue-facing copy. */
const formatVenueLoginError = (raw?: string | null, fallback = "Sign-in failed. Please try again.") => {
  const msg = String(raw || "").trim();
  if (!msg) return fallback;
  const lower = msg.toLowerCase();

  if (
    lower.includes("account inactive") ||
    lower.includes("inactive") ||
    lower.includes("deleted") ||
    lower.includes("account closed") ||
    lower.includes("account disabled")
  ) {
    return "This account is inactive or closed. OTP cannot be sent. Contact support if you need access restored.";
  }
  if (
    lower.includes("account not found") ||
    lower.includes("user not found") ||
    lower.includes("no user") ||
    lower.includes("email not found")
  ) {
    return "No venue account found for this email. Check the address, or register a new account.";
  }
  if (lower.includes("invalid password") || lower.includes("incorrect password") || lower.includes("wrong password")) {
    return "That password is incorrect. Try again, or use Forgot Password to reset it.";
  }
  if (lower.includes("invalid username or password") || lower.includes("invalid credentials")) {
    return "Email or password is incorrect. Double-check your details and try again.";
  }
  if (lower.includes("social login")) {
    return "This account signs in with a social provider, not email/password. Use the matching sign-in method, or contact support.";
  }
  if (
    lower.includes("organizer") ||
    lower.includes("not a venue") ||
    lower.includes("check your details") ||
    lower.includes("not authorized") ||
    lower.includes("access denied")
  ) {
    return "Unable to sign in with these credentials. Please check your details and try again.";
  }
  if (lower.includes("internal server")) {
    return "Something went wrong on our side. Please wait a moment and try again.";
  }
  if (lower === "please enter your email") {
    return "Enter the email linked to your venue account.";
  }
  if (lower === "please enter your password") {
    return "Enter your password to continue.";
  }
  return msg;
};

const LoginErrorAlert = ({ message }: { message: string }) => (
  <div className="login-error-alert" role="alert">
    <AlertCircle size={18} className="login-error-alert-icon" aria-hidden />
    <div className="login-error-alert-body">
      <span className="login-error-alert-title">Unable to sign in</span>
      <p className="login-error-alert-text">{message}</p>
    </div>
  </div>
);

/**
 * Venue login — same UI shell as organizer login.
 * Only persists session after confirming the user is a venue account (role 3).
 */
const LoginPage = (props: LoginPageProps) => {
  let navigate = useNavigate()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [errorMsg, setErrorMsg] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [loginWithPassword, setLoginWithPassword] = useState(true)
  const [otp, setOtp] = useState("")
  const [timer, setTimer] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState("");

  const showLoginError = (raw?: string | null, fallback?: string) => {
    setErrorMsg(formatVenueLoginError(raw, fallback));
  };

  const getApiErrorMessage = (err: any, fallback: string) =>
    err?.response?.data?.message || err?.response?.data?.error || fallback;

  useEffect(() => {
    document.body.classList.add('ex-theme');
  }, []);

  const handleSendOtp = async () => {
    if (!username) {
      showLoginError("Please enter your email");
      return;
    }
    try {
      setIsLoading(true);
      setError("");
      setErrorMsg("");
      // Stay on email step until OTP is actually issued
      setOtpSent(false);
      const response = await loginRequestOTP({ email: username });
      if (!response?.registrationId) {
        showLoginError(null, "Failed to start OTP session. Please try again.");
        return;
      }
      setRegistrationId(response.registrationId);
      if (response.email) {
        setEmail(response.email);
      }
      setOtpSent(true);
      setTimer(60);
    } catch (err: any) {
      console.error("Failed to request OTP:", err);
      setOtpSent(false);
      setRegistrationId(null);
      showLoginError(getApiErrorMessage(err, "Something went wrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!username) {
      showLoginError("Please enter your email");
      return;
    }
    try {
      setIsLoading(true);
      setError("");
      setErrorMsg("");
      const response = await loginRequestOTP({ email: username });
      if (!response?.registrationId) {
        showLoginError(null, "Failed to resend OTP. Please try again.");
        return;
      }
      setRegistrationId(response.registrationId);
      setTimer(60);
    } catch (err: any) {
      console.error("Failed to request OTP:", err);
      showLoginError(getApiErrorMessage(err, "Something went wrong"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  useEffect(() => {
    const storedToken = getAuthToken();
    if (storedToken) {
      navigate('/home');
    }
  }, [navigate]);

  /** Persist session only for confirmed venue accounts. */
  const completeVenueLogin = (response: any) => {
    if (!isVenueAccount(response.user)) {
      clearVenueSession();
      showLoginError(
        null,
        "Unable to sign in with these credentials. Please check your details and try again."
      );
      return false;
    }

    storeAuthToken(response.token);
    storeUserDetails(response.user);
    storeOrganizerId(Number(response?.user?.userId));
    localStorage.setItem("venueData", JSON.stringify(response.user));
    navigate("/home");
    return true;
  };

  const onClickOtpLogin = async () => {
    setErrorMsg("");
    if (!username) {
      showLoginError("Please enter your email");
      return;
    }
    if (!registrationId) {
      showLoginError(null, "OTP session expired. Request a new code and try again.");
      return;
    }
    if (otp.length !== 6) {
      showLoginError(null, "Enter the full 6-digit code sent to your email.");
      return;
    }
    setIsLoading(true)
    let data = {
      email: email || username,
      "registrationId": registrationId,
      "otp": otp
    }
    try {
      const response: any = await loginVerifyOTP(data);
      if (response) {
        if (!response.user || !response.token) {
          showLoginError(null, "Unexpected login response. Please try again.");
          return;
        }
        completeVenueLogin(response);
      }
    } catch (e: any) {
      showLoginError(getApiErrorMessage(e, "OTP verification failed. Please try again."));
    } finally {
      setIsLoading(false)
    }
  }

  const onClickLogin = async () => {
    setErrorMsg("");
    if (!username) {
      showLoginError("Please enter your email");
      return;
    }
    if (!password) {
      showLoginError("Please enter your password");
      return;
    }
    setIsLoading(true)
    let data = {
      "username": username,
      "password": password
    }
    try {
      const response: any = await userLogin(data);
      if (response) {
        if (!response.user || !response.token) {
          showLoginError(null, "Unexpected login response. Please try again.");
          return;
        }
        completeVenueLogin(response);
      }
    } catch (e: any) {
      showLoginError(getApiErrorMessage(e, "Sign-in failed. Please try again."));
    } finally {
      setIsLoading(false)
    }
  }

  const activeError = errorMsg || error;

  return (
    <div id="login-screen-wrapper">
      <Background />

      <div className="login-card">
        <div className="logo-container">
          <Link to="/">
            <img src={images.eventox_logo_dark} alt="Eventox Logo" className="logo-image" />
          </Link>
        </div>

        <p className="login-subtitle">
          Premium Venue Administration Dashboard
        </p>

        <div className="secure-badge">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" style={{ color: '#18C37E' }} />
          <span>Authorized venue directory node. Sign in to continue.</span>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="login-form">
          <div className="input-group-custom">
            <label className="label-custom">Your Email*</label>
            <div className="input-wrapper">
              <span className="input-icon-left">
                <Mail size={16} />
              </span>
              <input
                className="input-custom"
                type="email"
                placeholder="Enter your email"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (errorMsg || error) {
                    setErrorMsg("");
                    setError("");
                  }
                }}
              />
            </div>
          </div>

          {loginWithPassword ? (
            <div className="login-form" style={{ gap: '1.25rem' }}>
              <div className="input-group-custom">
                <div className="password-label-row">
                  <label className="label-custom">Password*</label>
                  <Link className="forgot-link-custom" to="/forgot-password">
                    Forgot Password?
                  </Link>
                </div>
                <div className="input-wrapper">
                  <span className="input-icon-left">
                    <Lock size={16} />
                  </span>
                  <input
                    className="input-custom"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errorMsg || error) {
                        setErrorMsg("");
                        setError("");
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {activeError && <LoginErrorAlert message={activeError} />}

              <button
                type="submit"
                className="btn-submit"
                disabled={isLoading}
                onClick={(e) => {
                  e.preventDefault();
                  onClickLogin();
                }}
              >
                {isLoading ? "Signing In..." : "Sign In to Eventox Workspace"}
              </button>
            </div>
          ) : (
            <div className="login-form" style={{ gap: '1.25rem' }}>
              {!otpSent ? (
                <div className="login-form" style={{ gap: '1.25rem' }}>
                  {activeError && <LoginErrorAlert message={activeError} />}
                  <button
                    type="button"
                    className="btn-submit"
                    disabled={isLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSendOtp();
                    }}
                  >
                    {isLoading ? "Sending OTP..." : "Send OTP"}
                  </button>
                  <div className="register-link-custom">
                    New to EventoX? <Link to="/register">Sign up</Link>
                  </div>
                </div>
              ) : (
                <div className="login-form" style={{ gap: '1rem' }}>
                  <div className="input-group-custom">
                    <label className="label-custom" style={{ textAlign: 'center', width: '100%' }}>
                      Enter OTP*
                    </label>
                    <div className="otp-input-container">
                      <OtpPinInput
                        value={otp}
                        onChange={setOtp}
                        boxStyle={{
                          border: "1px solid var(--login-input-border)",
                          background: "var(--login-input-bg)",
                          color: "var(--login-text-primary)",
                          height: 48,
                          borderRadius: 10,
                        }}
                      />
                    </div>
                  </div>

                  <div className="text-center" style={{ fontSize: '13px' }}>
                    {timer > 0 ? (
                      <p style={{ color: 'var(--login-text-muted)', margin: 0 }}>
                        Re-send available in {timer}s
                      </p>
                    ) : (
                      <p style={{ color: 'var(--login-text-secondary)', margin: 0 }}>
                        Didn&apos;t receive code?{" "}
                        <span
                          style={{ cursor: "pointer", fontWeight: "700", color: "#EE3F7C" }}
                          onClick={handleResendOtp}
                        >
                          Resend
                        </span>
                      </p>
                    )}
                  </div>

                  {activeError && <LoginErrorAlert message={activeError} />}

                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={isLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      onClickOtpLogin();
                    }}
                  >
                    {isLoading ? "Verifying..." : "Verify & Sign In"}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="toggle-container">
            <div
              className={`toggle-option ${loginWithPassword ? "active" : ""}`}
              onClick={() => {
                setLoginWithPassword(true);
                setErrorMsg("");
                setError("");
                setOtpSent(false);
                setOtp("");
              }}
            >
              Email and Password
            </div>
            <div
              className={`toggle-option ${!loginWithPassword ? "active" : ""}`}
              onClick={() => {
                setLoginWithPassword(false);
                setErrorMsg("");
                setError("");
              }}
            >
              Email OTP
            </div>
          </div>

          <div className="register-link-custom">
            New to EventoX? <Link to="/register">Sign up</Link>
          </div>
        </form>

        <div className="text-center" style={{ fontSize: '11px', color: 'var(--login-text-muted)', marginTop: '-0.25rem' }}>
          © {new Date().getFullYear()}, Eventox. All rights reserved.
        </div>
      </div>
    </div>
  );
}

export default LoginPage
